import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../services/notification.service';
import type { ErrorPayload } from '../../shared/models/system/error-payload.model';

/**
 * Request context set by the JWT interceptor while it owns the first 401.
 */
export const JWT_INTERCEPTOR_HANDLES_401 = new HttpContextToken<boolean>(() => false);

/**
 * Auth/session endpoints where a 401 is an expected control-flow response.
 */
const SILENT_401_URLS = ['api/auth/refresh'];

/**
 * Checks whether an HTTP error should be allowed to propagate without notifying the user.
 */
function shouldSuppressNotification(
  request: HttpRequest<unknown>,
  errorResponse: HttpErrorResponse,
): boolean {
  if (errorResponse.status !== 401) {
    return false;
  }

  return (
    request.context.get(JWT_INTERCEPTOR_HANDLES_401) ||
    SILENT_401_URLS.some((silentUrl) => request.url.includes(silentUrl))
  );
}

/**
 * Extracts a user-friendly error message from an HTTP error response.
 * Handles various error response formats from the backend.
 *
 * @param errorResponse - The HTTP error response
 * @returns A user-friendly error message
 */
function extractErrorMessage(errorResponse: HttpErrorResponse): string {
  const error = errorResponse.error;

  // Handle ErrorPayload format from backend
  if (error && typeof error === 'object' && 'errorMessages' in error) {
    const payload = error as ErrorPayload;
    if (payload.errorMessages?.length > 0) {
      return payload.errorMessages.join('\n');
    }
  }

  // Handle plain string error
  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  // Handle error object with message property
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }

  // Fallback to HTTP status text or generic message
  if (errorResponse.message) {
    return errorResponse.message;
  }

  return `Server error: ${errorResponse.status} ${errorResponse.statusText || 'Unknown error'}`;
}

/**
 * Processes HTTP errors and displays appropriate notifications.
 * Logs errors to console in development mode.
 *
 * @param errorResponse - The HTTP error response
 * @param notificationService - The notification service for displaying errors
 */
function processError(
  errorResponse: HttpErrorResponse,
  notificationService: NotificationService,
): void {
  // Log to console in development
  if (!environment.production) {
    console.error('HTTP Error:', errorResponse);
  }

  // 401 reaches here only after the JWT interceptor's refresh-and-retry has
  // already run (refresh succeeded but the retried request was still unauthorized,
  // or refresh failed outright). Surface a targeted message instead of swallowing it.
  if (errorResponse.status === 401) {
    notificationService.showServerError(
      'Access denied or your session has expired. Please reload the page or sign in again.',
    );
    return;
  }

  // Skip notification for cancelled requests (status 0)
  if (errorResponse.status === 0 && errorResponse.error instanceof ProgressEvent) {
    notificationService.showClientError('Network error: Unable to connect to server');
    return;
  }

  // Display error to user
  const message = extractErrorMessage(errorResponse);
  notificationService.showServerError(message);
}

/**
 * HTTP interceptor that catches all HTTP errors and displays them to users.
 *
 * This interceptor:
 * - Logs errors to console in development mode
 * - Displays user-friendly error messages via NotificationService
 * - Skips expected auth refresh 401 responses
 * - Handles network errors separately
 * - Re-throws errors to allow component-level handling if needed
 *
 * This is a functional interceptor following Angular 21+ patterns.
 */
export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);

  return next(request).pipe(
    catchError((errorResponse: unknown) => {
      if (errorResponse instanceof HttpErrorResponse) {
        if (!shouldSuppressNotification(request, errorResponse)) {
          processError(errorResponse, notificationService);
        }
      }
      return throwError(() => errorResponse);
    }),
  );
};
